import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { flow, pipe } from 'fp-ts/lib/function'
import { Collapse, Button, Tag } from 'antd'
import { record, array, option, boolean } from 'fp-ts'
import { FormattedMessage } from 'react-intl'
import { stepBackAction, stepForwardAction, requiredKeys } from './types'
import styled from 'styled-components'
import AssignColumnsToSheet from './AssignColumnsToSheet'
import { useMode } from '../Bills/queries'

const useAssignState = () => {
  const upload = useSelector(s => s.upload)
  return upload.state === 'Assign' ? upload : undefined
}

const ButtonRow = styled.div`
  width: 100%;
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
`

const TagContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
`

const STag = styled(Tag)`
  margin-right: 8px;
`

const MissingMessage = ({ data }: { data: Record<string, boolean> }) => {
  const messages = pipe(
    data,
    record.filter(v => !v),
    record.collect(k => (
      <STag color="red" key={k}>
        <FormattedMessage id="sheetIncomplete" values={{ name: k }} />
      </STag>
    ))
  )
  return messages.length === 0 ? null : <TagContainer>{messages}</TagContainer>
}

const EPanel = styled(Collapse.Panel)<{ errored: boolean }>`
  box-shadow: ${p => (p.errored ? '0 0 0 4px rgba(244, 67, 54, 0.2)' : 'none')};
  :first-child {
    border-top-left-radius: 8px !important;
    border-top-right-radius: 8px !important;
  }
`

const AssignColumns = () => {
  const mode = useMode()
  const upload = useAssignState()
  const sheetStatuses = React.useMemo(
    () =>
      pipe(
        upload?.assignments ?? {},
        record.map(v =>
          pipe(
            requiredKeys(mode),
            array.foldMap(boolean.MonoidAll)(k =>
              option.isSome(record.lookup(k, v))
            )
          )
        )
      ),
    [mode, upload?.assignments]
  )
  const sheetNames = upload?.selectedSheets ?? []
  const complete = pipe(
    sheetStatuses,
    record.foldMap(boolean.MonoidAll)(v => v)
  )
  const dispatch = useDispatch()

  return (
    <>
      <Collapse expandIconPosition="right" defaultActiveKey={sheetNames}>
        {pipe(
          sheetNames,
          array.map(name => (
            <EPanel errored={!sheetStatuses[name]} header={name} key={name}>
              <AssignColumnsToSheet sheetName={name} />
            </EPanel>
          ))
        )}
      </Collapse>
      <ButtonRow>
        <MissingMessage data={sheetStatuses} />
        <Button.Group>
          <Button onClick={flow(stepBackAction, dispatch)}>
            <FormattedMessage id="back" />
          </Button>
          <Button
            type="primary"
            disabled={!complete}
            onClick={() => dispatch(stepForwardAction(mode))}
          >
            <FormattedMessage id="next" />
          </Button>
        </Button.Group>
      </ButtonRow>
    </>
  )
}

export default AssignColumns
